import {
  all, call, put, select, takeEvery, takeLatest,
} from 'redux-saga/effects';
import findResult, {
  createOrderByParams,
  findWithCodes,
  findWithReadStatus,
  isValidFilter,
  successAndErrors,
  wildcardFilter,
} from '@/utils/resultsHelper';
import callAdapter, { batchAdapter } from '@/utils/sagaHelper';
import { states } from '@/constants/lifecycle';
import authActions from '@/auth/constants';
import selectors from '@/selectors';
import { applyTheme } from '@/themes/actions';
import { applyVocabularies } from '@/vocabularies/actions';
import { applyConfigurations } from '@/configurations/actions';
import applyWebsocket from '@/websocket/actions';
import { toggleOmniSearchFlags } from '@/search/actions';
import { CARDS_PAGE_SIZE, presentationOptions } from '@/constants/states';
import actions, { userLists } from '../constants';
import {
  fetchUserError,
  applyRole,
  showUserError,
  showUser,
  showUsers,
  showUsersError,
  fetchGuestUserError,
  applyGuestUser,
  updateApprovedSuccess,
  updateApprovedError,
  updateSearchableSuccess,
  updateSearchableError,
  toggleMyApproval,
  applyRoleActions,
  applyCurrentUser,
  applyUserGroups,
  archiveUsersSuccess,
  archiveUsersError,
  showUsersMetricsError,
  showUsersMetrics,
  toggleMyArchived,
} from '../actions';
import UsersService from '../service';

function* fetchUsers(action) {
  const items = yield select(selectors.users);
  const {
    display,
    filter: storeFilter,
    role,
    approvedFlag,
    archivedFlag,
    unapprovedFlag,
    pageSize,
    currentPage,
    orderBy,
  } = items[action.category];
  const requiresFilter = action.requiresFilter || false;
  const minFilterLength = action.minFilterLength || false;

  const filter = action.filter || storeFilter;
  if (!isValidFilter(filter, { requiresFilter, minFilterLength })) { return undefined; }

  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const includeApproved = action.approvedFlag || approvedFlag;
  const includeUnapproved = action.unapprovedFlag || unapprovedFlag;
  const includeArchived = action.archivedFlag || archivedFlag;
  const orderByParams = createOrderByParams(orderBy);

  const response = yield call(callAdapter, UsersService.users(
    wildcardFilter(filter),
    role,
    includeApproved,
    includeUnapproved,
    includeArchived,
    page,
    display === presentationOptions.CARDS ? CARDS_PAGE_SIZE : size,
    orderByParams,
  ));

  return { ...response, size };
}

export function* users(action) {
  const fetchUsersResponse = yield fetchUsers(action);
  if (!fetchUsersResponse) { return; }

  const { response, error, size } = fetchUsersResponse;
  if (error) {
    yield put(showUsersError(action.category, error));
  } else {
    yield put(
      showUsers(
        action.category,
        action.append,
        size,
        response.items,
        response.paging.total,
        response.paging.pages,
        response.paging.page,
      ),
    );
  }
}

export function* usersMetrics(action) {
  const fetchUsersResponse = yield fetchUsers(action);
  if (!fetchUsersResponse) { return; }

  const { response, error } = fetchUsersResponse;

  if (error) {
    yield put(showUsersMetricsError(action.category, error));
  } else {
    yield put(showUsersMetrics(action.category, response.items));
  }
}

export function* user({ category, code }) {
  const { response, error } = yield call(callAdapter, UsersService.user(code));

  if (error) {
    yield put(showUserError(category, code, error));
  } else {
    yield put(showUser(category, code, response));
  }
}

function* setOmniSearchFlags(permissions) {
  const searchRun = permissions.find((perm) => perm.category === 'search' && perm.action === 'run');
  const datasetRead = permissions.find((perm) => perm.category === 'datasets' && perm.action === 'read');
  if (!searchRun || !datasetRead) {
    yield put(toggleOmniSearchFlags('home', null, true, true, false));
  }
}

export function* signedInUser() {
  const { response, error } = yield call(callAdapter, UsersService.currentUser());

  if (error) {
    yield put(fetchUserError(error));
  } else {
    const {
      approved, role, permissions, preferences, role_actions: roleActions,
    } = response;
    yield put(applyCurrentUser(response));
    yield put(applyRole(approved, role, permissions));
    yield put(applyTheme(preferences.theme));
    yield put(applyVocabularies(preferences.vocabularies));
    yield put(applyConfigurations(preferences.configurations));
    yield put(applyWebsocket(preferences.websocket));
    yield put(applyRoleActions(roleActions));
    yield put(applyUserGroups(response.usergroups));
    yield setOmniSearchFlags(permissions);
  }
}

export function* guestUser() {
  const themes = yield select(selectors.themes);
  const { theme } = themes;
  const { response } = yield call(callAdapter, UsersService.guestUser({ theme }));

  if (response) {
    const {
      approved, role, permissions, preferences, role_actions: roleActions,
    } = response;

    yield put(applyGuestUser(response));
    yield put(applyRole(approved, role, permissions));
    yield put(applyTheme(preferences.theme));
    yield put(applyVocabularies(preferences.vocabularies));
    yield put(applyConfigurations(preferences.configurations));
    yield put(applyRoleActions(roleActions));
    yield setOmniSearchFlags(permissions);
  } else {
    yield put(fetchGuestUserError());
  }
}

export function* updateUserApproved({ category, code }) {
  const items = yield select(selectors.users);
  const { currentUser } = yield select(selectors.auth);
  const { uuid, approved, name } = findResult(items, category, code);
  const { response, error } = yield call(callAdapter, UsersService.updateApproved(uuid, !approved));

  if (error) {
    yield put(updateApprovedError(category, code, name, error));
  } else {
    yield put(
      updateApprovedSuccess(category, code, response, !approved),
    );
    if (currentUser.id === uuid) {
      yield put(toggleMyApproval());
    }
  }
}

export function* updateUserSearchable({ category, code }) {
  const items = yield select(selectors.users);
  const { uuid, searchable, name } = findResult(items, category, code);
  const { response, error } = yield call(callAdapter, UsersService.updateSearchable(uuid, !searchable));

  if (error) {
    yield put(updateSearchableError(category, code, name, error));
  } else {
    yield put(updateSearchableSuccess(category, code, response, !searchable));
  }
}

function* afterArchive(results, isArchival) {
  const { success, errors } = successAndErrors(results);

  if (errors.length > 0) {
    yield put(archiveUsersError(errors.map((e) => ({ ...e, isArchival }))));
  }

  if (success.length > 0) {
    const userItems = yield select(selectors.users);
    const codes = success.map((item) => item.code);
    const categories = findWithCodes(userItems, userLists, codes);
    const toRefresh = findWithReadStatus(userItems, categories, states.SUCCESS);
    const { currentUser } = yield select(selectors.auth);
    const currentUserArchived = isArchival && success.some((u) => u.uuid === currentUser.id);

    yield put(archiveUsersSuccess(success, isArchival));
    yield all(toRefresh.map((category) => users({ category })));

    if (currentUserArchived) {
      yield put(toggleMyArchived());
    }
  }
}

export function* archive({
  items,
  reason,
  disableTokens,
  archiveRequests,
}) {
  const results = yield all(items.map((item) => (
    batchAdapter(item, UsersService.archive(item, reason, disableTokens, archiveRequests)))));
  yield afterArchive(results, true);
}

export function* restore({ items }) {
  const results = yield all(items.map((item) => (batchAdapter(item, UsersService.restore(item)))));
  yield afterArchive(results, false);
}

export function* refreshUsersPresentation(action) {
  yield call(users, { ...action, page: 1, append: false });
}

export default function* usersApi() {
  yield takeEvery(
    [
      actions.FETCH_USERS,
      actions.REFRESH_USERS,
      actions.TOGGLE_USERS_COLUMN_ORDER_BY,
      actions.TOGGLE_USERS_SINGLE_SORTING,
      actions.UPDATE_USERS_FLAGS,
      actions.UPDATE_USERS_ROLE_FILTER,
    ],
    users,
  );
  yield takeEvery(actions.REFRESH_USERS_METRICS, usersMetrics);
  yield takeEvery(actions.FETCH_USER, user);
  yield takeLatest(authActions.LOGIN_SUCCESS, signedInUser);
  yield takeLatest(actions.FETCH_GUEST_USER, guestUser);
  yield takeEvery(actions.UPDATE_USER_APPROVED, updateUserApproved);
  yield takeEvery(actions.UPDATE_USER_SEARCHABLE, updateUserSearchable);
  yield takeEvery(actions.ARCHIVE_USERS, archive);
  yield takeEvery(actions.RESTORE_USERS, restore);
  yield takeEvery(actions.TOGGLE_USERS_PRESENTATION, refreshUsersPresentation);
}
